import React, { useContext, useMemo } from 'react'
import { Menu, Tooltip } from 'antd'
import type { MenuProps } from 'antd'
import * as Icons from '@ant-design/icons'
import { NavLink } from 'react-router-dom'

import { SidebarLogo, StyledSider } from '../styled'
import { useSidebar } from '../hooks'
import { useAuth } from '@modules/me/hooks'
import { CashRegisterStatusContext } from '../../cash-register/context/status'
import logo from '@shared/assets/logo.png'

export const AppSidebar = () => {
  const { isOpen, activeKey } = useSidebar()
  const { can, state } = useAuth()
  const cashRegisterStatus = useContext(CashRegisterStatusContext)

  const menuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'statistics',
        label: 'Ümumi',
        type: 'group',
        children: [
          {
            key: '/statistics',
            icon: <Icons.RocketOutlined />,
            label: <NavLink to='/statistics'>Piştaxta</NavLink>,
          },
          can(33) && {
            key: '/customers',
            icon: <Icons.TeamOutlined />,
            label: <NavLink to='/customers'>Müştərilər</NavLink>,
          },
          can(1) && !state.user?.data?.emergencyMode && {
            key: '/users',
            icon: <Icons.UserSwitchOutlined />,
            label: <NavLink to='/users'>İstifadəçilər</NavLink>,
          },
        ].filter(Boolean),
      },
      {
        key: 'appointments',
        label: 'Qəbul',
        type: 'group',
        children: [
          can(66) && {
            key: '/appointments/create',
            icon: <Icons.TeamOutlined />,
            label: <NavLink to='/appointments/create'>Qəbula yazılmaq</NavLink>,
          },
          can(64) && {
            key: '/appointments',
            icon: <Icons.TableOutlined />,
            label: <NavLink to='/appointments'>Qəbul reyestr</NavLink>,
          },
          {
            key: '/randevus',
            icon: <Icons.CalendarOutlined />,
            label: <NavLink to='/randevus'>Randevular</NavLink>,
          },
          can(64) && {
            key: '/appointments/calendar',
            icon: <Icons.CalendarOutlined />,
            label: <NavLink to='/appointments/calendar'>Randevu təqvimi</NavLink>,
          },
        ].filter(Boolean),
      },
      {
        key: 'cash-register',
        label: 'Kassa',
        type: 'group',
        children: [
          can(77) && !cashRegisterStatus.data?.connected && {
            key: '/cash-register',
            icon: <Icons.MoneyCollectOutlined />,
            disabled: true,
            label: (
              <Tooltip title='Kassa əməliyyatları üçün ekranın yuxarı hissəsində olan "Obyekt seç" düyməsindən istifadə etməklə obyektinizi seçin'>
                <NavLink to='/cash-register'>Kassa aparatı</NavLink>
              </Tooltip>
            ),
          },
          can(77) && cashRegisterStatus.data?.connected && {
            key: '/cash-register',
            icon: <Icons.MoneyCollectOutlined />,
            label: <NavLink to='/cash-register'>Kassa aparatı</NavLink>,
          },
          can(94) && {
            key: '/cash-register/checks',
            icon: <Icons.FileExclamationOutlined />,
            label: <NavLink to='/cash-register/checks'>Çap edilmiş çeklər</NavLink>,
          },
          can(109) && !state.user?.data?.emergencyMode && {
            key: '/cash-flow',
            icon: <Icons.FileExclamationOutlined />,
            label: <NavLink to='/cash-flow'>Cashflow</NavLink>,
          },
          can(71) && !state.user?.data?.emergencyMode && {
            key: '/transactions',
            icon: <Icons.TransactionOutlined />,
            label: <NavLink to='/transactions'>Tranzaksiyalar</NavLink>,
          },
        ].filter(Boolean),
      },
      [123, 121, 124, 129].some((permission) => can(permission)) && {
        key: 'warehouse',
        label: 'Anbar',
        type: 'group',
        children: [
          can(124) && {
            key: '/purchase-documents',
            icon: <Icons.DiffOutlined />,
            label: <NavLink to='/purchase-documents'>Alış sənədləri</NavLink>,
          },
          can(129) && {
            key: '/stock-adjustments',
            icon: <Icons.ToolOutlined />,
            label: <NavLink to='/stock-adjustments'>Anbar düzəlişləri</NavLink>,
          },
          {
            key: '/stock-movements',
            icon: <Icons.ToolOutlined />,
            label: <NavLink to='/stock-movements'>Stok hərəkətləri</NavLink>,
          },
          {
            key: '/stocks',
            icon: <Icons.StockOutlined />,
            label: <NavLink to='/stocks'>Stoklar</NavLink>,
          },
        ].filter(Boolean),
      },
      {
        key: 'catalogs',
        label: 'Kataloqlar',
        type: 'group',
        children: [
          can(38) && {
            key: '/products',
            icon: <Icons.OrderedListOutlined />,
            label: <NavLink to='/products'>Məhsullar</NavLink>,
          },
          can(48) && {
            key: '/services',
            icon: <Icons.SortAscendingOutlined />,
            label: <NavLink to='/services'>Xidmətlər</NavLink>,
          },
          can(123) && {
            key: '/departments',
            icon: <Icons.BankOutlined />,
            label: <NavLink to='/departments'>Şöbələr</NavLink>,
          },
          can(121) && {
            key: '/warehouses',
            icon: <Icons.InboxOutlined />,
            label: <NavLink to='/warehouses'>Anbarlar</NavLink>,
          },
          can(58) && {
            key: '/doctors',
            icon: <Icons.UsergroupAddOutlined />,
            label: <NavLink to='/doctors'>Həkimlər</NavLink>,
          },
          can(130) && {
            key: '/stock-adjustment-reasons',
            icon: <Icons.FormOutlined />,
            label: <NavLink to='/stock-adjustment-reasons'>Anbar düzəliş səbəbləri</NavLink>,
          },
          can(8) && {
            key: '/clinics',
            icon: <Icons.ClusterOutlined />,
            label: <NavLink to='/clinics'>Partnyor klinikalar</NavLink>,
          },
          can(64) && {
            key: '/insurances',
            icon: <Icons.PaperClipOutlined />,
            label: <NavLink to='/insurances'>Sığorta şirkətləri</NavLink>,
          },
          can(135) && {
            key: '/activity-logs',
            icon: <Icons.ProfileOutlined />,
            label: <NavLink to='/activity-logs'>Aktivlik logları</NavLink>,
          },
        ].filter(Boolean),
      },
      {
        key: 'settings',
        label: 'Proqram ayarları',
        type: 'group',
        children: [
          can(122) && {
            key: '/suppliers',
            icon: <Icons.TeamOutlined />,
            label: <NavLink to='/suppliers'>Təchizatçılar</NavLink>,
          },
          can(120) && {
            key: '/measurements',
            icon: <Icons.DashboardOutlined />,
            label: <NavLink to='/measurements'>Ölçü vahidləri</NavLink>,
          },
          can(18) && {
            key: '/status',
            icon: <Icons.BuildOutlined />,
            label: <NavLink to='/status'>Statuslar</NavLink>,
          },
          can(13) && {
            key: '/models',
            icon: <Icons.AppstoreOutlined />,
            label: <NavLink to='/models'>Modellər</NavLink>,
          },
          can(28) && !state.user?.data?.emergencyMode && {
            key: '/groups',
            icon: <Icons.GroupOutlined />,
            label: <NavLink to='/groups'>Qruplar</NavLink>,
          },
          can(23) && {
            key: '/operations',
            icon: <Icons.SettingOutlined />,
            label: <NavLink to='/operations'>Əməliyyatlar</NavLink>,
          },
        ].filter(Boolean),
      },
    ].filter(Boolean) as MenuProps['items'],
    [can, state.user?.data?.emergencyMode, cashRegisterStatus.data?.connected],
  )

  return (
    <StyledSider trigger={null} collapsible={true} width={224} collapsedWidth={46} collapsed={!isOpen}>
      <SidebarLogo>
        <img src={logo} alt='ArtroMed Logo' />
      </SidebarLogo>
      <Menu theme='light' mode='inline' selectedKeys={[activeKey]} items={menuItems} />
    </StyledSider>
  )
}
